import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  useQpmPestCreateMutation,
  useQpmPestListQuery,
  useQpmPestPutSynonymsMutation,
  useQpmPestRenameMutation,
  useQpmPestUpdateAbbreviationMutation,
  useQpmPestUpdateDescriptionMutation,
  useQpmPestUpdateTechnicalTermMutation,
} from "@/services/backend/qpm/pests/api/pest-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  CreatePestReq,
  PestItem,
} from "@/services/backend/qpm/pests/types/pest";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Pencil, Plus, Save, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { Input } from "@/shared/components/ui/input";
import { PestKind } from "@/services/backend/qpm/pests/types/pest-enum";
import { MonitorDefaultsCard } from "@/routes/base-data/processes/qpm/base-data/_components/monitor-defaults-card";
import { PestKindUtil } from "@/services/backend/qpm/pests/lib/pest-kind-util";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

export function BaseDataRoutePage() {
  // TODO-LT: It's not intended to create new monitor types manually, but different IoT Classes should be able to be created manually by the user
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Monitor Grundwerte")}</CardTitle>
        <CardDescription>
          {t("Hier können Sie die Standardwerte für neue Monitore bearbeiten.")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <MonitorDefaultsCard />
      </CardContent>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>{t("Schädlings Kategorien")}</CardTitle>
          <PersistenceStateUsingContext />
        </div>
        <CardDescription>
          {t(
            "Hier können Sie die verschiedenen Schädlings Kategorien bearbeiten.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <PestCategoryList />
      </CardContent>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>{t("Schädlings Indikatoren")}</CardTitle>
          <PersistenceStateUsingContext />
        </div>
        <CardDescription>
          {t(
            "Hier können Sie die verschiedenen Schädlings Indikatoren bearbeiten.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <PestTypeList />
      </CardContent>
    </Card>
  );
}

function PestCategoryList() {
  return <PestListTable kind={PestKind.Category} />;
}

function PestTypeList() {
  return <PestListTable kind={PestKind.Indicator} />;
}

const parseSynonyms = (input: string): string[] =>
  input
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

function PestListTable({ kind }: { kind: PestKind }) {
  const { companyId } = useActiveCompany();
  const {
    data: list,
    error,
    isLoading,
  } = useQpmPestListQuery({
    company: { active: true, values: [companyId] },
    kind,
  });

  const [create, createState] = useQpmPestCreateMutation();
  const [rename, renameState] = useQpmPestRenameMutation();
  const [updateAbbreviation, updateAbbreviationState] =
    useQpmPestUpdateAbbreviationMutation();
  const [updateDescription, updateDescriptionState] =
    useQpmPestUpdateDescriptionMutation();
  const [updateTechnicalTerm, updateTechnicalTermState] =
    useQpmPestUpdateTechnicalTermMutation();
  const [putSynonyms, putSynonymsState] = useQpmPestPutSynonymsMutation();

  const items: PestItem[] = useMemo(() => list?.items ?? [], [list?.items]);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.name.localeCompare(b.name)),
    [items],
  );

  const existingKeys = useMemo(
    () => new Set(sortedItems.map((p) => toNormalizedKey(p.name))),
    [sortedItems],
  );

  const [newRowId, setNewRowId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const isCreatingNew = editingId != null && editingId === newRowId;

  const [request, setRequest] = useState<CreatePestReq>({
    companyId,
    kind,
    name: "",
    key: "",
    technicalTerm: "",
    abbreviation: "",
    description: "",
    synonyms: [],
  });
  const [synonymsInput, setSynonymsInput] = useState<string>("");
  const [descriptionInput, setDescriptionInput] = useState<string>("");
  const [technicalTermInput, setTechnicalTermInput] = useState<string>("");

  // Keep request/synonymsInput in sync when switching edit row.
  // Important: don't include controlled input state or `request.*` in deps,
  // otherwise this effect will re-run while typing and overwrite user input.
  useEffect(() => {
    if (!editingId) return;

    if (newRowId && editingId === newRowId) {
      setRequest({
        companyId,
        kind,
        name: "",
        key: "",
        technicalTerm: "",
        abbreviation: "",
        description: "",
        synonyms: [],
      });
      setSynonymsInput("");
      setDescriptionInput("");
      setTechnicalTermInput("");
      return;
    }

    const row = sortedItems.find((p) => p.id === editingId);
    if (!row) return;

    setRequest({
      companyId,
      kind,
      name: row.name,
      key: toDocKeyInputValue(row.name),
      technicalTerm: row.technicalTerm ?? "",
      abbreviation: row.abbreviation ?? "",
      description: row.description ?? "",
      synonyms: row.synonyms ?? [],
    });

    setSynonymsInput((row.synonyms ?? []).join(", "));
    setDescriptionInput(row.description ?? "");
    setTechnicalTermInput(row.technicalTerm ?? "");
  }, [editingId, newRowId, sortedItems, kind, companyId]);

  const onCancel = () => {
    setEditingId(null);
    setNewRowId(null);
    setSynonymsInput("");
    setDescriptionInput("");
    setRequest({
      companyId,
      kind,
      name: "",
      key: "",
      technicalTerm: "",
      abbreviation: "",
      description: "",
      synonyms: [],
    });
    setTechnicalTermInput("");
  };

  const validateRequest = (req: CreatePestReq): string | null => {
    if (!req.name || req.name.trim().length === 0) return t("Name fehlt");

    const strictKey = toNormalizedKey(req.key ?? req.name);
    if (strictKey.length === 0) return t("Bezeichner fehlt");

    if (isCreatingNew) {
      if (existingKeys.has(strictKey)) {
        return t("Bezeichner bereits vorhanden");
      }
    }

    return null;
  };

  const onSave = async () => {
    const validationError = validateRequest(request);
    if (validationError) return;

    if (isCreatingNew) {
      // Backend create doesn't include synonyms/description yet in this UI. We'll add it later when needed.
      await create({
        companyId,
        kind,
        name: request.name,
        key: request.key,
        technicalTerm: technicalTermInput,
        abbreviation: request.abbreviation ?? "",
        description: descriptionInput,
        synonyms: parseSynonyms(synonymsInput),
      }).unwrap();
      setEditingId(null);
      setNewRowId(null);
      setSynonymsInput("");
      setDescriptionInput("");
      setTechnicalTermInput("");
    } else if (editingId) {
      await rename({ id: editingId, name: request.name }).unwrap();

      await updateAbbreviation({
        id: editingId,
        abbreviation: request.abbreviation ?? "",
      }).unwrap();

      await updateDescription({
        id: editingId,
        description: descriptionInput,
      }).unwrap();

      await updateTechnicalTerm({
        id: editingId,
        technicalTerm: technicalTermInput,
      }).unwrap();

      // Save synonyms for existing row.
      await putSynonyms({
        id: editingId,
        synonyms: parseSynonyms(synonymsInput),
      }).unwrap();

      setEditingId(null);
      setSynonymsInput("");
      setDescriptionInput("");
      setTechnicalTermInput("");
    }
  };

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  const canAddRow = newRowId == null && editingId == null;

  return (
    <div className="flex flex-col gap-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[22%]">{t("Name")}</TableHead>
            <TableHead className="w-[12%]">{t("Bezeichner")}</TableHead>
            <TableHead className="w-[16%]">{t("Fachbegriff")}</TableHead>
            <TableHead className="w-[10%]">{t("Abkürzung")}</TableHead>
            <TableHead className="w-[22%]">{t("Beschreibung")}</TableHead>
            <TableHead className="w-[14%]">{t("Synonyme")}</TableHead>
            <TableHead className="w-[8%] text-right">
              {t("Bearbeiten")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedItems.map((pest) => (
            <PestRow
              key={pest.id}
              kind={kind}
              pest={pest}
              isEditMode={editingId === pest.id}
              isNew={false}
              request={request}
              setRequest={setRequest}
              onEdit={() => setEditingId(pest.id)}
              onCancel={onCancel}
              onSave={onSave}
              existingKeys={existingKeys}
              synonymsInput={synonymsInput}
              setSynonymsInput={setSynonymsInput}
              descriptionInput={descriptionInput}
              setDescriptionInput={setDescriptionInput}
              technicalTermInput={technicalTermInput}
              setTechnicalTermInput={setTechnicalTermInput}
            />
          ))}

          {newRowId && editingId === newRowId && (
            <PestRow
              key={newRowId}
              kind={kind}
              pest={{
                id: newRowId,
                key: request.key ?? "",
                kind,
                name: "",
                technicalTerm: "",
                abbreviation: "",
                description: "",
                synonyms: [],
                createdAt: "",
                updatedAt: "",
              }}
              isEditMode
              isNew
              request={request}
              setRequest={setRequest}
              onEdit={() => setEditingId(newRowId)}
              onCancel={onCancel}
              onSave={onSave}
              existingKeys={existingKeys}
              synonymsInput={synonymsInput}
              setSynonymsInput={setSynonymsInput}
              descriptionInput={descriptionInput}
              setDescriptionInput={setDescriptionInput}
              technicalTermInput={technicalTermInput}
              setTechnicalTermInput={setTechnicalTermInput}
            />
          )}

          {sortedItems.length === 0 && !newRowId && (
            <TableRow>
              <TableCell colSpan={7} className="text-muted-foreground py-6">
                {t("Keine Einträge gefunden.")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-start">
        <Button
          disabled={!canAddRow}
          onClick={() => {
            const id = v4();
            setNewRowId(id);
            setEditingId(id);
            setRequest({
              companyId,
              kind,
              name: "",
              key: "",
              technicalTerm: "",
              abbreviation: "",
              description: "",
              synonyms: [],
            });
            setTechnicalTermInput("");
            setSynonymsInput("");
            setDescriptionInput("");
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {PestKindUtil.toString(kind)} {t("hinzufügen")}
        </Button>
      </div>

      {(createState.error ||
        renameState.error ||
        updateAbbreviationState.error ||
        updateDescriptionState.error ||
        updateTechnicalTermState.error ||
        putSynonymsState.error) && (
        <RTKQueryErrorAlert
          error={
            (createState.error ||
              renameState.error ||
              updateAbbreviationState.error ||
              updateDescriptionState.error ||
              updateTechnicalTermState.error ||
              putSynonymsState.error)!
          }
        />
      )}
    </div>
  );
}

function PestRow({
  kind,
  pest,
  isEditMode,
  isNew,
  request,
  setRequest,
  onEdit,
  onCancel,
  onSave,
  existingKeys,
  synonymsInput,
  setSynonymsInput,
  descriptionInput,
  setDescriptionInput,
  technicalTermInput,
  setTechnicalTermInput,
}: {
  kind: PestKind;
  pest: PestItem;
  isEditMode: boolean;
  isNew: boolean;
  request: CreatePestReq;
  setRequest: (req: CreatePestReq) => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  existingKeys: Set<string>;
  synonymsInput: string;
  setSynonymsInput: (val: string) => void;
  descriptionInput: string;
  setDescriptionInput: (val: string) => void;
  technicalTermInput: string;
  setTechnicalTermInput: (val: string) => void;
}) {
  const [keyInput, setKeyInput] = useState<string>("");
  const [keyTouched, setKeyTouched] = useState<boolean>(false);

  useEffect(() => {
    if (!isEditMode) return;
    setKeyInput(
      String((isNew ? request.key : toDocKeyInputValue(pest.name)) ?? ""),
    );
    setKeyTouched(false);
  }, [isEditMode, isNew, request.key, pest.name]);

  const setName = (name: string) => {
    setRequest({ ...request, kind, name });

    // Auto-derive key only for new rows and only until user touched the key manually.
    if (!isNew || keyTouched) return;

    const derived = toDocKeyInputValue(name);
    if (!derived) return;

    const derivedStrict = toNormalizedKey(derived);
    if (derivedStrict.length === 0) return;

    // Prevent auto-setting duplicates (user can still adjust manually)
    if (existingKeys.has(derivedStrict)) return;

    setKeyInput(derived);
    setRequest({
      ...request,
      kind,
      name,
      key: derived,
    });
  };

  // In edit mode we show the controlled value. In view mode we show backend value from the row.
  const desc = isEditMode ? descriptionInput : (pest.description ?? "");
  const technicalTerm = isEditMode
    ? technicalTermInput
    : (pest.technicalTerm ?? "");

  const synonyms = isEditMode
    ? parseSynonyms(synonymsInput)
    : (pest.synonyms ?? []);

  const firstSynonym = synonyms[0] ?? "";
  const restSynonyms = synonyms.slice(1);

  const renderSynonymsMainCell = () => {
    if (synonyms.length === 0) return "-";

    if (restSynonyms.length === 0) {
      return (
        <span className="truncate" title={firstSynonym}>
          {firstSynonym}
        </span>
      );
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help underline decoration-dotted underline-offset-2">
              {firstSynonym}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div className="max-w-[420px]">
              <div className="font-medium">{t("Weitere Synonyme")}</div>
              <div className="mt-1 break-words">{restSynonyms.join(", ")}</div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const synonymsMainCell = renderSynonymsMainCell();

  // Editing synonyms stays in an additional row, but only when editing.
  const synonymsRow = isEditMode ? (
    <TableRow>
      <TableCell colSpan={8} className="text-muted-foreground py-2 text-sm">
        <div className="flex flex-col gap-1">
          <div className="text-foreground font-medium">{t("Synonyme")}</div>
          <Input
            type="text"
            placeholder={t("Kommagetrennt, z.B. Ratte, Wanderratte")}
            value={synonymsInput}
            onChange={(e) => setSynonymsInput(e.target.value)}
          />
        </div>
      </TableCell>
    </TableRow>
  ) : null;

  if (isEditMode) {
    return (
      <>
        <TableRow>
          <TableCell className="py-1.5">
            <Input
              type="text"
              placeholder={t("Name")}
              value={request.name ?? ""}
              onChange={(e) => setName(e.target.value)}
            />
          </TableCell>
          <TableCell className="py-1.5">
            <Input
              type="text"
              placeholder={t("Bezeichner")}
              value={isNew ? keyInput : toDocKeyInputValue(pest.name)}
              disabled={!isNew}
              onChange={(e) => {
                if (!isNew) return;

                setKeyTouched(true);

                const raw = e.target.value;
                const typed = toDocKeyInputValue(raw);
                setKeyInput(typed);

                const maybeDuplicate =
                  typed.length > 0 && existingKeys.has(toNormalizedKey(typed));

                setRequest({
                  ...request,
                  kind,
                  key: maybeDuplicate ? request.key : typed,
                });
              }}
            />
          </TableCell>
          <TableCell className="py-1.5">
            <Input
              type="text"
              placeholder={t("Fachbegriff")}
              value={technicalTermInput}
              onChange={(e) => setTechnicalTermInput(e.target.value)}
            />
          </TableCell>
          <TableCell className="py-1.5">
            <Input
              type="text"
              placeholder={t("Abkürzung")}
              value={request.abbreviation ?? ""}
              onChange={(e) =>
                setRequest({
                  ...request,
                  kind,
                  abbreviation: e.target.value,
                })
              }
            />
          </TableCell>
          <TableCell className="py-1.5">
            <Input
              type="text"
              placeholder={t("Beschreibung")}
              value={descriptionInput}
              onChange={(e) => setDescriptionInput(e.target.value)}
            />
          </TableCell>
          <TableCell className="py-1.5">{synonymsMainCell}</TableCell>
          <TableCell className="py-1.5 text-right">
            <div className="flex items-center justify-end">
              <TooltipProvider>
                {/* Cancel */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="mr-1"
                      size="sm"
                      variant="outline"
                      onClick={onCancel}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("Abbrechen")}</p>
                  </TooltipContent>
                </Tooltip>

                {/* Save */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="default" onClick={onSave}>
                      <Save className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("Speichern")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </TableCell>
        </TableRow>
        {synonymsRow}
      </>
    );
  }

  return (
    <TableRow>
      <TableCell className="py-1.5 font-medium">{pest.name}</TableCell>
      <TableCell className="py-1.5">
        {toNormalizedKey(pest.key ?? pest.name)}
      </TableCell>
      <TableCell className="py-1.5">
        {technicalTerm.trim().length > 0 ? (
          <span className="truncate" title={technicalTerm}>
            {technicalTerm}
          </span>
        ) : (
          "-"
        )}
      </TableCell>
      <TableCell className="py-1.5">{pest.abbreviation || "-"}</TableCell>
      <TableCell className="py-1.5">
        {desc.trim().length > 0 ? (
          <span className="truncate" title={desc}>
            {desc}
          </span>
        ) : (
          "-"
        )}
      </TableCell>
      <TableCell className="py-1.5">{synonymsMainCell}</TableCell>
      <TableCell className="py-1.5 text-right">
        <div className="flex items-center justify-end">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={onEdit}>
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("Bearbeiten")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </TableCell>
    </TableRow>
  );
}

function toDocKeyInputValue(input: string): string {
  let s = input.toLowerCase();

  s = s
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");

  s = s.replace(/\s+/g, "-");
  s = s.replace(/_+/g, "-");
  s = s.replace(/[^a-z0-9-]/g, "");
  s = s.replace(/-+/g, "-");
  return s;
}

function toNormalizedKey(input: string): string {
  let s = toDocKeyInputValue(input);
  s = s.replace(/^-+/g, "").replace(/-+$/g, "");
  return s;
}
